<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.quests', 2),
                        href: null,
                    },
                ]"
            />
        </template>
        <div class="md:max-w-md mx-auto">
            <WeeklyQuestMilestones :weekly-milestones="weeklyMilestones" />
        </div>

        <div class="mt-5">
            <AlertMessage class="mt-5 inline-block" type="info">
                <template v-slot:body>
                    <div class="ml-3 text-lg">
                        <IconHourglassEmpty class="w-6 h-6 text-gray-400 inline-block" />
                        <span>
                            {{
                                $t('messages.X_quests_refill_in_time', {
                                    amount: refill.quests_amount,
                                })
                            }}
                            &nbsp;
                        </span>
                        <span>
                            <CountdownTimer class="inline-block" :datetime="refill.refills_at" />
                        </span>
                    </div>
                </template>
            </AlertMessage>
        </div>

        <div class="mt-5">
            <AppWell>
                <div class="flex flex-col gap-4">
                    <div v-for="quest in quests" :key="quest.id" class="">
                        <AppCard class="">
                            <div class="flex justify-between gap-x-4 items-center px-2">
                                <div class="flex-none w-20">
                                    <div class="">
                                        <QuestProgress :progress="quest.progress">
                                            <span class="text-sm">
                                                {{ quest.progress.current }} / {{ quest.progress.total }}
                                            </span>
                                        </QuestProgress>
                                    </div>
                                </div>
                                <div class="grow md:flex gap-2">
                                    <div>
                                        <div class="md:text-base text-xs">{{ quest.text }}</div>
                                        <div class="md:flex flex-wrap gap-2">
                                            <div v-if="quest.difficulty_display">
                                                <BadgePill :color="quest.difficulty_display.color">
                                                    {{ quest.difficulty_display.text }}</BadgePill
                                                >
                                            </div>
                                            <div v-if="quest.is_once_per_day">
                                                <BadgePill color="gray" class="text-xs">
                                                    {{ $t('messages.is_once_per_day') }}
                                                </BadgePill>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <AppButton
                                            element="InertiaLink"
                                            :href="quest.links.update"
                                            v-if="quest.is_claimable"
                                            as="button"
                                            method="patch"
                                            desired-action
                                        >
                                            {{ $t('messages.claim_reward') }}
                                        </AppButton>
                                    </div>
                                </div>
                                <div class="flex-none">
                                    <div class="flex gap-x-1 items-center">
                                        <img class="h-6 w-6" src="/assets/gamification/currency/coin.svg" alt="coins" />
                                        {{ quest.reward.coins }}
                                    </div>
                                    <div class="flex gap-x-1 items-center mt-1">
                                        <img
                                            class="h-6 w-6"
                                            src="/assets/gamification/currency/emerald.svg"
                                            alt="coins"
                                        />
                                        {{ quest.reward.experience }}
                                    </div>
                                </div>
                            </div>
                        </AppCard>
                    </div>
                </div>
            </AppWell>
        </div>
        <Modal :show="showModal" @close="closeModal">
            <div class="flex flex-col items-center gap-2">
                <div class="text-xl font-semibold">
                    {{ $t('messages.congratulations!') }}
                </div>
                <div v-if="modalValues.type === 'quest'">
                    <div class="font-semibold">{{ $t('messages.quest_accomplished') }}:</div>
                    <div class="italic">"{{ modalValues.text }}"</div>
                </div>
                <div v-else-if="modalValues.type === 'milestone'">
                    <TreasureChest :type="modalValues.chest_type" class="object-contain h-36" :is-open="true" />
                </div>
                <div>
                    <div>{{ $t('messages.you_have_obtained') }}:</div>
                    <div class="mt-1">
                        <div class="flex gap-2 mt-2">
                            <div>
                                <BadgePill color="green" class="text-lg" v-if="modalValues.experience_amount !== null">
                                    {{ modalValues.experience_amount }}
                                </BadgePill>
                            </div>
                            <div>
                                <BadgePill color="red" class="text-lg" v-if="modalValues.health_amount !== null">
                                    {{ modalValues.health_amount }}
                                </BadgePill>
                            </div>
                            <div>
                                <BadgePill color="yellow" class="text-lg" v-if="modalValues.coins_amount !== null">
                                    {{ modalValues.coins_amount }}
                                </BadgePill>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-auto-animate class="grid grid-cols-3 lg:grid-cols-6 md:gap-6 mt-5 w-full">
                    <div v-for="(item, index) in modalValues.items" :key="index">
                        <div class="relative h-40">
                            <img :src="'/img/gamification/shine.svg'" alt="" class="absolute animate-spin-slow" />
                            <img :src="item.image_path" alt="" class="absolute object-contain" />
                        </div>
                    </div>
                    <div class="relative h-40">&nbsp;</div>
                </div>
            </div>
        </Modal>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import WeeklyQuestMilestones from '@/Pages/Student/Gamification/Quests/partials/WeeklyQuestMilestones.vue';
import { IconHourglassEmpty } from '@tabler/icons-vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import CountdownTimer from '@/components/CountdownTimer.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import QuestProgress from '@/Pages/Student/Gamification/Quests/partials/QuestProgress.vue';
import BadgePill from '@/components/BadgePill.vue';
import AppButton from '@/components/AppButton.vue';
import Modal from '@/components/Modal.vue';
import TreasureChest from '@/components/TreasureChest.vue';
import { Howl } from 'howler';

export default {
    components: {
        TreasureChest,
        Modal,
        AppButton,
        BadgePill,
        QuestProgress,
        AlertMessage,
        CountdownTimer,
        AppCard,
        AppWell,
        WeeklyQuestMilestones,
        PanelPage,
        IconHourglassEmpty,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'weeklyMilestones', 'refill', 'quests'],
    mounted() {
        if (this.meta.info.claimed_quest) {
            this.showQuestClaimedModal(this.meta.info.claimed_quest);
        }
        if (this.meta.info.claimed_milestone) {
            this.showMilestoneClaimedModal(this.meta.info.claimed_milestone);
        }
    },
    watch: {
        'meta.info.claimed_quest': {
            handler(newVal) {
                if (newVal) {
                    this.showQuestClaimedModal(newVal);
                }
            },
            deep: true,
            immediate: true,
        },
        'meta.info.claimed_milestone': {
            handler(newVal) {
                if (newVal) {
                    this.showMilestoneClaimedModal(newVal);
                }
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        showQuestClaimedModal(values) {
            this.modalValues.type = 'quest';
            this.modalValues.text = values.text;
            this.modalValues.experience_amount = values.experience_amount;
            this.modalValues.health_amount = values.health_amount;
            this.modalValues.coins_amount = values.coins_amount;
            this.modalValues.items = [];
            this.showModal = true;
            this.addItems(values.items);
        },
        showMilestoneClaimedModal(values) {
            this.modalValues.type = 'milestone';
            this.modalValues.text = values.text;
            this.modalValues.experience_amount = values.experience_amount;
            this.modalValues.health_amount = values.health_amount;
            this.modalValues.coins_amount = values.coins_amount;
            this.modalValues.items = [];
            this.showModal = true;
            this.addItems(values.items);
            setTimeout(() => {
                const sound = new Howl({
                    src: ['/assets/gamification/open_chest.mp3'],
                });
                sound.play();
            }, 1000);
        },
        addItems(items) {
            items.forEach((item, i) => {
                setTimeout(
                    () => {
                        this.modalValues.items.push(item);
                    },
                    1000 + 500 * i,
                );
            });
        },
        closeModal() {
            this.showModal = false;
            this.modalValues.coins_amount = {};
        },
    },
    data() {
        return {
            modalValues: {},
            showModal: false,
        };
    },
};
</script>
